import { useMemo, useCallback } from 'react';

import AnalyticsService, { EventNameAMP, ParamNameAMP } from 'application/services/AnalyticsService';
import { useAppSelector } from 'application/providers/StoreProvider';
import { selectIsInstantAdcampaignAvailable } from 'application/store';

import { AdCampaignTypeEnum } from '@entities/media';

import { DropdownItemDataType } from '@zvuk-b2b/react-uikit/ui/Dropdown';

import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { UseAdTypeSelectControllerProps } from './types';
import { AD_TYPE_MAP } from './consts';
import AdTypeSelectOptionContent from './AdTypeSelectOptionContent';

const useAdTypeSelectController = ({
  value,
  initialValue,
  isInstantAdCampaignDisabled,
  isInstantDisabledHintLinkDisabled,
  onChange,
  setPromoTab,
  mediaType,
}: UseAdTypeSelectControllerProps) => {
  const isInstantAdcampaignAvailable =
    useAppSelector(selectIsInstantAdcampaignAvailable) && mediaType !== AdCampaignMediaTypeEnum.Video;

  const onOptionClick = useCallback(
    (type: string) => {
      onChange(type as AdCampaignTypeEnum);

      AnalyticsService.event(EventNameAMP.AD_CAMPAIGN_TYPE_SELECTED, {
        [ParamNameAMP.AD_CAMPAIGN_TYPE]: type,
      });
    },
    [onChange]
  );

  const adCompanyTypeOptions: DropdownItemDataType[] = useMemo(() => {
    const adTypeMap = { ...AD_TYPE_MAP };

    if (!isInstantAdcampaignAvailable) {
      delete adTypeMap[AdCampaignTypeEnum.Instant];
    }

    if (mediaType === AdCampaignMediaTypeEnum.Video) {
      delete adTypeMap[AdCampaignTypeEnum.Period];
    }

    const result: DropdownItemDataType[] = Object.entries(adTypeMap).map(([type, title]) => {
      const isDisabled = type === AdCampaignTypeEnum.Instant && isInstantAdCampaignDisabled;

      return {
        selected: value === type,
        disabled: isDisabled,
        content: (
          <AdTypeSelectOptionContent
            value={value}
            type={type}
            isDisabled={isDisabled}
            isLinkDisabled={isInstantDisabledHintLinkDisabled}
            title={title}
            onClick={setPromoTab}
          />
        ),
        key: `select_ad_type_${type}`,
        onClick: () => {
          onOptionClick(type);
        },
      };
    });

    return result;
  }, [
    value,
    isInstantAdcampaignAvailable,
    isInstantAdCampaignDisabled,
    isInstantDisabledHintLinkDisabled,
    mediaType,
    setPromoTab,
    onOptionClick,
    AD_TYPE_MAP,
    initialValue,
  ]);

  return {
    adCompanyTypeOptions,
    currentValue: AD_TYPE_MAP[value],
  };
};

export default useAdTypeSelectController;
